<template>
  <div class="providers-page">
    <!-- Hero Section -->
    <section class="hero-section hero-animate" :style="{ backgroundImage: `url(${heroBackgroundImage})` }">
      <div class="container">
        <div class="row">
          <div class="col-lg-10 mx-auto text-center">
            <h1 class="hero-title">
              <strong>Conviértete</strong> en nuestro <strong>agente de ventas</strong>
            </h1>
            <h3 class="hero-subtitle">Construye tu futuro financiero mientras ayudas a tu comunidad</h3>
            <p class="hero-description">
              Genera ingresos reales vendiendo planes de salud que tu comunidad necesita. Comisiones atractivas desde el primer cliente, sin límites de ganancia. Territorio exclusivo, soporte dedicado y herramientas profesionales. Miles de familias están esperando lo que tienes que ofrecer. Tu oportunidad de cambiar tu vida financiera empieza aquí.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Programa 1: comisiones + simulador 24m + link PatientFlow -->
    <AgentsCommissionsSection />

    <!-- Form Section -->
    <section id="form-section" class="form-section" :style="{ backgroundImage: `url(${formBackgroundImage})` }">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-8">
            <div class="register-card animate-on-scroll fade-in-scale">
              <h2 class="form-title">Comienza a ganar dinero hoy mismo</h2>
              
              <form @submit.prevent="handleSubmit">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="nombre" class="form-label">Nombre</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="nombre"
                      v-model="form.nombre"
                    />
                  </div>
                  
                  <div class="col-md-6 mb-3">
                    <label for="apellido" class="form-label">Apellido</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="apellido"
                      v-model="form.apellido"
                    />
                  </div>
                </div>
                
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="telefono" class="form-label">Teléfono <span class="required-asterisk">*</span></label>
                    <PhoneField
                      v-model="form.telefono"
                      :country.sync="form.phoneCountry"
                      input-id="telefono"
                      required
                      :error="phoneError"
                      @blur="validatePhone"
                    />
                  </div>
                  
                  <div class="col-md-6 mb-3">
                    <label for="email" class="form-label">Email</label>
                    <input 
                      type="email" 
                      class="form-control" 
                      id="email"
                      v-model="form.email"
                    />
                  </div>
                </div>
                
                <fieldset class="agent-type mb-4">
                  <legend class="form-label">Tipo de Agente <span class="required-asterisk">*</span></legend>
                  <div class="agent-type-options">
                    <label
                      v-for="type in agentTypes"
                      :key="type.rolId"
                      class="agent-type-card"
                      :class="{ 'is-selected': form.rol_id === type.rolId }"
                    >
                      <input
                        v-model="form.rol_id"
                        class="agent-type-input"
                        type="radio"
                        name="rol_id"
                        :value="type.rolId"
                        required
                      />
                      <span class="agent-type-check" aria-hidden="true">
                        <i v-if="form.rol_id === type.rolId" class="fas fa-check"></i>
                      </span>
                      <span class="agent-type-name">{{ type.name }}</span>
                      <span class="agent-type-description">{{ type.description }}</span>
                    </label>
                  </div>
                </fieldset>
                
                <div class="form-check mb-4">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    id="consent"
                    v-model="form.consent"
                    required
                  />
                  <label class="form-check-label" for="consent">
                    Consiento recibir notificaciones SMS, alertas y comunicaciones de marketing ocasionales de <strong>Hispano Medical Plan</strong>. La frecuencia de los mensajes varía. Pueden aplicarse tarifas de mensajes y datos. Envía HELP al <strong>214-888-9000</strong> para obtener ayuda. Puedes responder STOP para cancelar la suscripción en cualquier momento.
                  </label>
                </div>
                
                <ul v-if="submitErrors.length" class="submit-errors" role="alert">
                  <li v-for="message in submitErrors" :key="message">{{ message }}</li>
                </ul>

                <p v-if="selectedAgentType" class="agent-type-reminder">
                  Te vas a registrar como <strong>{{ selectedAgentType.name }}</strong>
                </p>

                <button 
                  type="submit" 
                  class="btn btn-primary btn-lg btn-block btn-shimmer"
                  :disabled="loading"
                >
                  <span v-if="loading">
                    <i class="fas fa-spinner fa-spin mr-2"></i>
                    Procesando...
                  </span>
                  <span v-else>
                    Comenzar a ganar ahora
                  </span>
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Approval Process Section -->
    <section class="approval-process-section">
      <div class="container">
        <div class="section-header text-center mb-5 animate-on-scroll fade-in-up">
          <h2 class="section-title">Proceso de Aprobación</h2>
          <p class="section-subtitle">
            Pasos para unirte al equipo de agentes de <strong>Hispano Medical Plan</strong>
          </p>
        </div>
        
        <div class="row align-items-center">
          <div class="col-lg-6">
            <div class="row">
              <div class="col-12 mb-2" v-for="(step, index) in approvalSteps" :key="index">
                <div class="process-step animate-on-scroll fade-in-up" :class="{ 'step-highlighted': index === 2 }" :style="`animation-delay: ${index * 0.1}s`">
                  <div class="step-icon">
                    <img :src="step.icon" :alt="step.title" />
                  </div>
                  <h4 class="step-title">{{ step.title }}</h4>
                </div>
              </div>
            </div>
          </div>
          <div class="col-lg-6 mb-4 mb-lg-0">
            <div class="approval-image animate-on-scroll fade-in-right">
              <img :src="approvalImage" alt="Proceso de Aprobación" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Ready to Start Section -->
    <section class="ready-section" :style="{ backgroundImage: `url(${readySectionImage})` }">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-8 mx-auto text-center">
            <div class="ready-content animate-on-scroll fade-in-up">
              <h2 class="ready-title">¡Listo para comenzar!</h2>
              <p class="ready-description">
                <strong>Tu oportunidad de generar ingresos reales empieza ahora.</strong> Únete a la red de agentes de 
                <strong>Hispano Medical Plan</strong> y conviértete en el referente de salud de tu comunidad. 
                <strong>Comisiones atractivas desde el primer cliente</strong>, sin límites de ganancia. 
                <strong>Capacitación completa incluida</strong> - no necesitas experiencia, solo ganas de triunfar. 
                <strong>Herramientas profesionales, soporte dedicado y un mercado en crecimiento.</strong> 
                <strong>Miles de familias están esperando lo que tienes que ofrecer. ¿Estás listo para cambiar tu vida financiera?</strong>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Benefits Section -->
    <section class="benefits-section">
      <div class="container">
        <div class="section-header text-center mb-5 animate-on-scroll fade-in-up">
          <h2 class="section-title">Beneficios para Agentes de Hispano Medical Plan</h2>
          <p class="section-subtitle">
            Más que un trabajo, es tu oportunidad de construir un negocio propio con ingresos ilimitados. Cada venta es dinero en tu bolsillo, cada cliente es tu patrimonio. Únete a los agentes que ya están transformando su futuro financiero.
          </p>
        </div>
        
        <div class="row">
          <div class="col-md-6 mb-4" v-for="(benefit, index) in benefits" :key="index">
            <div class="benefit-item animate-on-scroll fade-in-up" :style="`animation-delay: ${index * 0.1}s`">
              <div class="benefit-icon">
                <img :src="benefit.icon" :alt="benefit.title" />
              </div>
              <div class="benefit-card">
                <h4>{{ benefit.title }}</h4>
                <p>{{ benefit.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Resources Section -->
    <section class="resources-section">
      <div class="container">
        <div class="section-header text-center mb-5 animate-on-scroll fade-in-up">
          <h2 class="section-title">Recursos y Herramientas para Agentes</h2>
          <p class="section-subtitle">
            Todo lo que necesitas para vender más y ganar más. Herramientas profesionales que los mejores agentes usan todos los días. No inventes la rueda, úsala para construir tu éxito.
          </p>
        </div>
        
        <div class="row">
          <div class="col-md-6 col-lg-4 mb-4" v-for="(resource, index) in resources" :key="index">
            <div class="resource-card animate-on-scroll fade-in-up card-hover-smooth" :style="`animation-delay: ${index * 0.1}s`">
              <div class="resource-icon">
                <i :class="resource.icon"></i>
              </div>
              <h4 class="resource-title">{{ resource.title }}</h4>
              <ul class="resource-list">
                <li v-for="(item, idx) in resource.items" :key="idx">{{ item }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA PDF Drive (mockup) -->
    <AgentsCtaBand />

    <!-- FAQ Section -->
    <section class="faq-section">
      <div class="container">
        <div class="section-header text-center mb-5 animate-on-scroll fade-in-up">
          <h2 class="section-title">Preguntas Frecuentes</h2>
          <p class="section-subtitle">
            Respuestas a las consultas más comunes sobre ser agente
          </p>
        </div>
        
        <div class="row justify-content-center">
          <div class="col-lg-10">
            <div class="faq-accordion">
              <div 
                v-for="(faq, index) in faqs" 
                :key="index"
                class="faq-item animate-on-scroll fade-in-up"
                :style="`animation-delay: ${index * 0.1}s`"
              >
                <div 
                  class="faq-question"
                  @click="toggleFaq(index)"
                  :class="{ active: faq.isOpen }"
                >
                  <div class="faq-question-content">
                    <span class="faq-number">{{ index + 1 }}.</span>
                    <span class="faq-question-text">{{ faq.question }}</span>
                  </div>
                  <i 
                    class="fas fa-chevron-up faq-arrow"
                    :class="{ rotated: faq.isOpen }"
                  ></i>
                </div>
                <transition name="slide-fade">
                  <div v-show="faq.isOpen" class="faq-answer">
                    <p>{{ faq.answer }}</p>
                  </div>
                </transition>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import plansService from '@/core/services/api/landings/plans'
import ToastificationContent from '@/core/plugins/toastification/ToastificationContent.vue'
import { AgentsCommissionsSection, AgentsCtaBand } from '../components'
import { scrollToProvidersFormReliable } from '../mixins/scrollToProvidersForm'
import { registerApiMessages } from '../utils/registerUserPlanHelpers'
import { isValidPhone, toBackendPhone } from '@/core/utils/phone'
import PhoneField from '../components/PhoneField.vue'

export default {
  name: "Providers",
  components: { ToastificationContent, AgentsCommissionsSection, AgentsCtaBand, PhoneField },
  data() {
    return {
      heroBackgroundImage: require('@/assets/lp/2p.png'),
      formBackgroundImage: require('@/assets/lp/3p.png'),
      approvalImage: require('@/assets/lp/4.png'),
      readySectionImage: require('@/assets/lp/5.png'),
      form: {
        nombre: "",
        apellido: "",
        telefono: "",
        phoneCountry: "US",
        email: "",
        rol_id: "",
        consent: false
      },
      phoneError: "",
      visibleElements: new Set(),
      loading: false,
      submitErrors: [],
      agentTypes: [
        {
          rolId: '9',
          name: 'Agente Plan',
          description: 'Vendes los planes de Hispano Medical Plan y ganas comisión por cada venta.'
        },
        {
          rolId: '7',
          name: 'Insurance Agent',
          description: 'Refieres pacientes con seguro médico a la clínica. No vendes planes.'
        }
      ],
      approvalSteps: [
        {
          icon: require('@/assets/lp/Recurso 1@300x.webp'),
          title: 'Llenado de la forma para registro'
        },
        {
          icon: require('@/assets/lp/Recurso 2@300x.webp'),
          title: 'Confirmación y validación por correo'
        },
        {
          icon: require('@/assets/lp/Recurso 3@300x.webp'),
          title: 'Recibe el enlace por correo'
        },
        {
          icon: require('@/assets/lp/Recurso 4@300x.webp'),
          title: 'Acceso a tu panel de afiliado'
        },
        {
          icon: require('@/assets/lp/Recurso 5@300x.webp'),
          title: 'Monitoreo y seguimiento de resultados'
        }
      ],
      benefits: [
        {
          icon: require('@/assets/lp/Recurso 8@300x.webp'),
          title: 'Capacitación Continua',
          description: 'No vendas a ciegas. Recibe entrenamiento especializado que te convierte en experto. Certificaciones que validan tu conocimiento, webinars exclusivos y actualizaciones constantes. Cada día serás mejor vendedor, y mejores vendedores ganan más dinero.'
        },
        {
          icon: require('@/assets/lp/Recurso 9@300x.webp'),
          title: 'Soporte de Marketing',
          description: 'No vendas solo, vende con poder. Materiales profesionales listos para usar, campañas digitales que generan leads calificados, y herramientas diseñadas específicamente para la comunidad hispana. Tú cierras, nosotros te damos los clientes potenciales.'
        },
        {
          icon: require('@/assets/lp/Recurso 10@300x.webp'),
          title: 'Tecnología Avanzada',
          description: 'Vende desde cualquier lugar, en cualquier momento. CRM que organiza tus clientes automáticamente, app móvil que cierra ventas en minutos, y plataformas que te muestran exactamente cuánto estás ganando. La tecnología que necesitas para maximizar tus ingresos.'
        },
        {
          icon: require('@/assets/lp/Recurso 11@300x.webp'),
          title: 'Soporte Administrativo',
          description: 'Tu equipo de respaldo está listo 24/7. Soporte bilingüe que entiende tu mercado, procesamiento rápido de aplicaciones para que no pierdas ventas, y servicio al cliente que mantiene felices a tus clientes. Tú vendes, nosotros hacemos el resto.'
        }
      ],
      resources: [
        {
          icon: 'fas fa-desktop',
          title: 'Plataforma Digital',
          items: [
            'CRM que organiza tus clientes y oportunidades automáticamente',
            'Cotizador instantáneo que cierra ventas en segundos',
            'Procesamiento digital que elimina papeleos y acelera comisiones',
            'Dashboard en tiempo real que te muestra exactamente cuánto ganas'
          ]
        },
        {
          icon: 'fas fa-bullhorn',
          title: 'Marketing',
          items: [
            'Brochures profesionales que venden por ti',
            'Presentaciones que convierten visitantes en clientes',
            'Contenido para redes sociales que genera leads calificados',
            'Campañas de email que nutren tu cartera sin esfuerzo'
          ]
        },
        {
          icon: 'fas fa-mobile-alt',
          title: 'Aplicación Móvil',
          items: [
            'Vende desde tu celular, en cualquier momento y lugar',
            'Cotizaciones instantáneas que impresionan a tus clientes',
            'Gestiona tu cartera completa desde la palma de tu mano',
            'Ve tus comisiones en tiempo real, cada venta cuenta'
          ]
        },
        {
          icon: 'fas fa-book',
          title: 'Recursos',
          items: [
            'Manuales completos que te convierten en experto del producto',
            'Videos de capacitación que puedes ver cuando quieras',
            'Webinars exclusivos con estrategias de los mejores vendedores',
            'Casos de éxito reales que te enseñan cómo cerrar más ventas'
          ]
        },
        {
          icon: 'fas fa-tools',
          title: 'Soporte Técnico',
          items: [
            'Soporte 24/7 que nunca te deja solo cuando necesitas cerrar',
            'Chat en vivo que resuelve tus dudas en minutos',
            'Asistencia técnica remota que soluciona problemas al instante',
            'Entrenamiento personalizado que acelera tu curva de aprendizaje'
          ]
        },
        {
          icon: 'fas fa-chart-bar',
          title: 'Análisis y Reportes',
          items: [
            'Métricas que te muestran exactamente qué está funcionando',
            'Análisis de territorio que identifica tus mejores oportunidades',
            'Reportes de comisiones transparentes y en tiempo real',
            'Proyecciones de ingresos que te motivan a superar tus metas'
          ]
        }
      ],
      faqs: [
        {
          question: '¿Cómo es el proceso de registro?',
          answer: 'El proceso es muy sencillo. Primero completas el formulario de registro y recibirás un correo electrónico con las instrucciones. Luego deberás llenar y firmar los documentos requeridos, incluyendo el contrato y el formulario W-9 con tu información bancaria. Una vez verificados los datos, recibirás un segundo correo con el acceso a tu portal de afiliado, y finalmente un miembro de nuestro equipo se pondrá en contacto contigo para coordinar tu proceso de onboarding.',
          isOpen: false
        },
        {
          question: '¿Qué tipo de soporte recibo como nuevo agente?',
          answer: 'Nuestro equipo de soporte te acompaña en todo momento, ofreciéndote asistencia sobre los beneficios de los planes, los procesos de registro de los miembros, las herramientas de venta y los materiales promocionales. Además, contarás con capacitaciones y sesiones de seguimiento para ayudarte a crecer en el programa.',
          isOpen: false
        },
        {
          question: '¿Cómo puedo revisar mis ingresos o comisiones?',
          answer: 'A través del portal de afiliado, podrás consultar en tiempo real tus comisiones, los pagos procesados y el estado de tus clientes activos en tu cartera.',
          isOpen: false
        },
        {
          question: '¿Cuándo recibo mis primeras comisiones?',
          answer: 'Los pagos se procesan puntualmente cada 30 días, según las comisiones generadas y aprobadas durante el período anterior.',
          isOpen: false
        },
        {
          question: '¿Puedo trabajar medio tiempo como agente?',
          answer: 'Sí, puedes desempeñarte a tiempo parcial o a tiempo completo. Nuestro programa es flexible y se adapta a tu disponibilidad y ritmo de trabajo.',
          isOpen: false
        },
        {
          question: '¿Necesito experiencia previa en ventas o en el área médica?',
          answer: 'No necesariamente. Ofrecemos capacitación completa y recursos de apoyo para que cualquier persona con motivación y compromiso pueda desarrollarse con éxito como agente.',
          isOpen: false
        },
        {
          question: '¿Recibo materiales o herramientas de marketing para mis ventas?',
          answer: 'Sí, los agentes registrados tienen acceso a un kit digital con presentaciones, folletos, enlaces personalizados y materiales promocionales que facilitan la venta y captación de nuevos miembros.',
          isOpen: false
        },
        {
          question: '¿Hispano Medical Plan es algún tipo de seguro médico?',
          answer: 'No, Hispano Medical Plan no es un seguro médico. Es un plan de membresía de salud diseñado para ofrecer atención médica primaria a bajo costo a través de nuestra red de clínicas aliadas. Los servicios se brindan directamente en nuestras clínicas y centros afiliados, sin intermediarios ni aseguradoras, por lo que Hispano Medical Plan no efectúa pagos a proveedores externos como lo haría un seguro tradicional.',
          isOpen: false
        },
        {
          question: '¿Necesito tener una licencia de agente para vender los planes de Hispano Medical Plan?',
          answer: 'No, no necesitas una licencia de agente para comercializar los planes de Hispano Medical Plan. Nuestro programa de afiliados está diseñado para que cualquier persona pueda participar, siempre y cuando complete el proceso de registro y la capacitación inicial. Proporcionamos toda la información, herramientas y materiales necesarios para que puedas promover los planes de forma profesional y ética.',
          isOpen: false
        }
      ]
    };
  },
  computed: {
    selectedAgentType() {
      return this.agentTypes.find((type) => type.rolId === this.form.rol_id) || null;
    }
  },
  mounted() {
    this.setupScrollAnimations();
    this.scrollToHashTarget();
  },
  watch: {
    'form.telefono'() {
      if (this.phoneError) this.validatePhone();
    },
    '$route.hash'(hash) {
      if (hash) this.scrollToHashTarget();
    }
  },
  methods: {
    scrollToHashTarget() {
      const hash = (this.$route.hash || window.location.hash || '').replace(/^#/, '')
      if (hash !== 'form-section') return
      this.$nextTick(() => scrollToProvidersFormReliable())
    },
    setupScrollAnimations() {
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            this.visibleElements.add(entry.target);
          }
        });
      }, observerOptions);

      this.$nextTick(() => {
        const elements = this.$el.querySelectorAll('.animate-on-scroll');
        elements.forEach(el => observer.observe(el));
      });
    },
    scrollToForm(e) {
      if (e && e.preventDefault) e.preventDefault();
      this.scrollToHashTarget();
    },
    toggleFaq(index) {
      this.faqs[index].isOpen = !this.faqs[index].isOpen;
    },
    validatePhone() {
      if (!this.form.telefono.trim()) {
        this.phoneError = 'Ingresa tu teléfono.';
      } else {
        this.phoneError = isValidPhone(this.form.telefono, this.form.phoneCountry) ? '' : 'Ingresa un teléfono válido.';
      }
      return !this.phoneError;
    },
    async handleSubmit() {
      if (this.loading) return;
      
      // Validate that all required fields are complete
      if (!this.form.telefono || !this.form.rol_id || !this.form.consent) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Campos requeridos',
            icon: 'AlertCircleIcon',
            variant: 'warning',
            text: 'Por favor completa todos los campos requeridos.'
          }
        })
        return
      }
      if (!this.validatePhone()) return;

      this.loading = true;
      this.submitErrors = [];

      try {
        // Preparar los datos según el formato del endpoint
        const dataToSend = {
          name: this.form.nombre || '',
          lastname: this.form.apellido || '',
          phone: toBackendPhone(this.form.telefono, this.form.phoneCountry),
          email: this.form.email || '',
          rol_id: parseInt(this.form.rol_id)
        };

        const registeredAs = this.selectedAgentType.name;
        await plansService.registerUserPlan(dataToSend);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: '¡Éxito!',
            icon: 'CheckIcon',
            variant: 'success',
            text: `¡Cuenta de ${registeredAs} creada! Revisa tu correo electrónico para ver tu contraseña.`
          }
        })

        // Limpiar el formulario
        this.form = {
          nombre: "",
          apellido: "",
          telefono: "",
          phoneCountry: "US",
          email: "",
          rol_id: "",
          consent: false
        };
      } catch (error) {
        this.submitErrors = registerApiMessages(error, 'Error al procesar el registro. Por favor intenta de nuevo.')
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'No se pudo completar el registro',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: this.submitErrors.join(' ')
          }
        })
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../styles/variables';

.providers-page {
  .hero-section {
    background-size: cover;
    background-position: center;
    padding: 80px 0;
    color: white;
    position: relative;
    
    .container {
      position: relative;
      z-index: 2;
    }
    
    .hero-title {
      font-size: 4rem;
      font-weight: 400;
      margin-bottom: 1.5rem;
      color: rgba(255, 255, 255, 0.95);
      text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(0, 0, 0, 0.2);
      
      strong {
        font-weight: 700;
        font-size: 1.15em;
      }
    }
    
    .hero-subtitle {
      font-size: 1.8rem;
      opacity: 1;
      color: $landing-primary-color;
      font-weight: 700;
      margin-bottom: 1.5rem;
      display: inline-block;
      padding: 8px 20px;
      background-color: $landing-secondary-color;
      border-radius: 6px;
    }
    
    .hero-description {
      font-size: 1.5rem;
      color: white;
      font-weight: 600;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
      margin-bottom: 2rem;
      max-width: 900px;
      margin-left: auto;
      margin-right: auto;
    }
    
    .hero-buttons {
      margin-top: 2rem;
      
      .btn-primary-hero {
        background: $landing-primary-color;
        color: white;
        border: none;
        border-radius: 50px;
        padding: 18px 45px;
        font-size: 1.2rem;
        font-weight: 700;
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        transition: all 0.3s ease;
        box-shadow: 0 8px 25px $landing-primary-rgba-40;
        
        &:hover {
          background: $landing-primary-dark;
          transform: translateY(-3px) scale(1.05);
          box-shadow: 0 12px 35px $landing-primary-rgba-50;
          color: white;
          text-decoration: none;
        }
      }
    }
  }
  
  .form-section {
    padding: 10px;
    background-size: 80% auto;
    background-position: top center;
    background-repeat: no-repeat;
    position: relative;
    min-height: 1000px;
    
    .container {
      padding-top: 50vh;
    }
    
      .register-card {
      background: white;
      padding: 3rem;
      border-radius: 15px;
      box-shadow: 0 10px 30px rgba(45, 106, 110, 0.1);
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      margin-top: 0;
      
      &:hover {
        box-shadow: 0 15px 40px rgba(45, 106, 110, 0.12);
        transform: translateY(-5px);
      }
      
      .form-title {
        font-size: 2.5rem;
        font-weight: 700;
        color: $landing-primary-color;
        margin-bottom: 2rem;
        text-align: center;
      }
      
      .form-label {
        font-weight: 600;
        color: $landing-primary-color;
        margin-bottom: 0.5rem;
        transition: color 0.3s ease;
        font-size: 1.1rem;
        display: block;
      }
      
      input.form-control,
      ::v-deep .phone-field .form-control {
        border: 2px solid $landing-border-light;
        border-radius: 8px;
        padding: 12px 15px;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        color: $landing-text-dark !important;
        font-size: 1.05rem;
        font-weight: 500;
        background-color: white !important;
        line-height: 1.5;
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        
        &::placeholder {
          color: $landing-text-medium;
          opacity: 0.7;
        }
        
        &:focus {
          border-color: $landing-primary-color;
          box-shadow: 0 0 0 0.2rem $landing-primary-rgba-25;
          transform: translateY(-2px);
          color: $landing-text-dark !important;
          background-color: white !important;
          outline: none;
        }
      }
      
      .agent-type {
        border: 0;
        padding: 0;
        margin: 0;

        legend {
          width: auto;
        }
      }

      .agent-type-options {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 14px;

        @media (max-width: 575px) {
          grid-template-columns: 1fr;
        }
      }

      .agent-type-card {
        position: relative;
        display: grid;
        gap: 6px;
        margin: 0;
        padding: 18px 48px 18px 18px;
        border: 2px solid $landing-border-light;
        border-radius: 12px;
        background: white;
        cursor: pointer;
        transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;

        &:hover {
          border-color: $landing-secondary-color;
        }

        &:focus-within {
          box-shadow: 0 0 0 0.2rem $landing-primary-rgba-25;
        }

        &.is-selected {
          border-color: $landing-primary-color;
          background: rgba(34, 207, 224, 0.08);
          box-shadow: 0 6px 18px rgba(13, 43, 69, 0.12);
        }
      }

      .agent-type-input {
        position: absolute;
        opacity: 0;
        width: 1px;
        height: 1px;
      }

      .agent-type-check {
        position: absolute;
        top: 16px;
        right: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        border: 2px solid $landing-border-light;
        border-radius: 50%;
        font-size: 0.7rem;
      }

      .is-selected .agent-type-check {
        border-color: $landing-primary-color;
        background: $landing-primary-color;
        color: white;
      }

      .agent-type-name {
        color: $landing-primary-color;
        font-size: 1.15rem;
        font-weight: 700;
      }

      .agent-type-description {
        color: $landing-text-medium;
        font-size: 0.98rem;
        line-height: 1.5;
      }

      .agent-type-reminder {
        margin: 0 0 1rem;
        color: $landing-text-medium;
        text-align: center;

        strong {
          color: $landing-primary-color;
        }
      }

      .btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
      
      .form-check-label {
        font-size: 1.05rem;
        color: $landing-primary-color;
        line-height: 1.7;
        font-weight: 500;
        cursor: pointer;
        
        strong {
          color: $landing-primary-color;
          font-weight: 700;
        }
      }
      
      .required-asterisk {
        color: #F64E60;
        font-weight: 700;
        margin-left: 2px;
      }

      .submit-errors {
        margin: 0 0 1rem;
        padding: 0.75rem 1rem 0.75rem 2rem;
        border: 1px solid #F64E60;
        border-radius: 8px;
        background: #FFF5F6;
        color: #B42335;
        font-weight: 600;
      }
      
      .btn {
        padding: 15px;
        font-size: 1.1rem;
        font-weight: 600;
        border-radius: 8px;
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        
        &.btn-primary {
          background: $landing-primary-color;
          border-color: $landing-primary-color;
          color: white;
          
          &:hover:not(:disabled) {
            background: $landing-primary-dark;
            border-color: $landing-primary-dark;
            transform: translateY(-2px) scale(1.02);
            box-shadow: 0 8px 20px $landing-primary-rgba-40;
          }
        }
        
        &:hover:not(:disabled) {
          transform: translateY(-2px) scale(1.02);
        }
      }
    }
  }
  
  .approval-process-section {
    padding: 100px 0;
    background: white;
    
    .section-title {
      font-size: 3.5rem;
      font-weight: 700;
      color: $landing-primary-color;
      margin-bottom: 1.5rem;
    }
    
    .section-subtitle {
      font-size: 1.4rem;
      color: $landing-primary-color;
      font-weight: 500;
      margin-bottom: 3rem;
    }
    
    .approval-image {
      img {
        width: 100%;
        height: auto;
        max-width: none;
        border-radius: 15px;
      }
    }
    
    .process-step {
      display: flex;
      align-items: center;
      padding: 1.5rem;
      position: relative;
      background: white;
      border: 2px solid $landing-border-light;
      border-radius: 15px;
      transition: all 0.3s ease;
      
      &.step-highlighted {
        background: $landing-primary-color;
        border-color: $landing-primary-color;
        
        .step-title {
          color: white;
        }
      }
      
      &:hover {
        border-color: $landing-primary-color;
        box-shadow: 0 5px 15px $landing-primary-rgba-15;
      }
      
      .step-icon {
        width: 100px;
        height: 100px;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 1.5rem;
        transition: all 0.3s ease;
        
        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }
      
      .step-title {
        font-size: 1.5rem;
        font-weight: 700;
        color: $landing-text-dark;
        line-height: 1.4;
        margin: 0;
      }
    }
  }
  
  .ready-section {
    padding: 100px 0;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    min-height: 600px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    .container {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 500px;
    }
    
    .ready-content {
      text-align: center;
      width: 100%;
      
      .ready-title {
        font-size: 3.5rem;
        font-weight: 700;
        color: white;
        margin-bottom: 2rem;
        text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(0, 0, 0, 0.2);
      }
      
      .ready-description {
        font-size: 1.6rem;
        color: white;
        line-height: 1.8;
        font-weight: 700;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
        margin-bottom: 0;
        max-width: 900px;
        margin-left: auto;
        margin-right: auto;
      }
    }
  }
  
  .benefits-section {
    padding: 100px 0;
    background: $landing-bg-light;
    
    .section-title {
      font-size: 3.5rem;
      font-weight: 700;
      color: $landing-primary-color;
      margin-bottom: 1.5rem;
    }
    
    .section-subtitle {
      font-size: 1.4rem;
      color: $landing-primary-color;
      font-weight: 500;
      margin-bottom: 3rem;
    }
    
    .benefit-item {
      display: flex;
      align-items: center;
      padding: 0;
      background: transparent;
      border-radius: 15px;
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      border: none;
      height: 100%;
      
      &:hover {
        transform: translateX(10px);
        
        .benefit-icon img {
          transform: scale(1.1);
        }
        
        .benefit-card {
          border-color: $landing-primary-color;
          box-shadow: 0 10px 30px $landing-primary-rgba-20;
        }
      }
      
      .benefit-icon {
        flex-shrink: 0;
        width: 150px;
        height: 150px;
        margin-right: 2rem;
        display: flex;
        align-items: center;
        justify-content: center;
        
        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
      }
      
      .benefit-card {
        flex: 1;
        padding: 2rem;
        background: white;
        border: 2px solid $landing-border-light;
        border-radius: 15px;
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        
        h4 {
          font-size: 1.75rem;
          font-weight: 700;
          color: $landing-primary-color;
          margin-bottom: 1rem;
        }
        
        p {
          color: $landing-primary-color;
          line-height: 1.7;
          font-size: 1.15rem;
          font-weight: 500;
        }
      }
    }
  }
  
  .resources-section {
    padding: 100px 0;
    background: white;
    
    .section-title {
      font-size: 3.5rem;
      font-weight: 700;
      color: $landing-primary-color;
      margin-bottom: 1.5rem;
    }
    
    .section-subtitle {
      font-size: 1.4rem;
      color: $landing-primary-color;
      font-weight: 500;
      margin-bottom: 3rem;
    }
    
    .resource-card {
      background: white;
      border-radius: 15px;
      padding: 2rem;
      border: 2px solid $landing-border-light;
      transition: all 0.3s ease;
      height: 100%;
      
      &:hover {
        transform: translateY(-5px);
        box-shadow: 0 10px 30px $landing-primary-rgba-15;
        border-color: $landing-primary-color;
        
        .resource-icon {
          transform: scale(1.1) rotate(5deg);
        }
      }
      
      .resource-icon {
        width: 70px;
        height: 70px;
        background: $landing-primary-gradient;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 1.5rem;
        transition: all 0.3s ease;
        
        i {
          font-size: 2rem;
          color: white;
        }
      }
      
      .resource-title {
        font-size: 1.5rem;
        font-weight: 700;
        color: $landing-text-dark;
        margin-bottom: 1.25rem;
      }
      
      .resource-list {
        list-style: none;
        padding: 0;
        margin: 0;
        
        li {
          color: $landing-text-dark;
          font-size: 1.05rem;
          line-height: 1.8;
          font-weight: 500;
          margin-bottom: 0.75rem;
          padding-left: 1.5rem;
          position: relative;
          
          &::before {
            content: '✓';
            position: absolute;
            left: 0;
            color: $landing-primary-color;
            font-weight: 700;
          }
        }
      }
    }
  }
  
  .faq-section {
    padding: 100px 0;
    background: $landing-bg-light;
    
    .section-title {
      font-size: 3.5rem;
      font-weight: 700;
      color: $landing-primary-color;
      margin-bottom: 1.5rem;
    }
    
    .section-subtitle {
      font-size: 1.4rem;
      color: $landing-text-dark;
      font-weight: 500;
      margin-bottom: 3rem;
    }
    
    .faq-accordion {
      .faq-item {
        margin-bottom: 1.5rem;
        background: white;
        border: 2px solid $landing-border-light;
        border-radius: 12px;
        overflow: hidden;
        transition: all 0.3s ease;
        
        &:hover {
          border-color: $landing-primary-color;
          box-shadow: 0 5px 15px $landing-primary-rgba-15;
        }
      
        .faq-question {
          padding: 1.5rem 2rem;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
          transition: all 0.3s ease;
          background: white;
          
          &:hover {
            background: $landing-bg-light;
          }
          
          &.active {
            background: linear-gradient(135deg, $landing-bg-light 0%, rgba(232, 244, 248, 1) 100%);
            border-bottom: 2px solid $landing-border-light;
          }
          
          .faq-question-content {
            display: flex;
            align-items: center;
            gap: 15px;
            flex: 1;
            
            .faq-number {
              font-size: 1.3rem;
              font-weight: 700;
              color: $landing-primary-color;
              min-width: 30px;
            }
            
            .faq-question-text {
              font-size: 1.5rem;
              font-weight: 600;
              color: $landing-text-dark;
              line-height: 1.5;
            }
          }
          
          .faq-arrow {
            color: $landing-primary-color;
            font-size: 1.2rem;
            transition: transform 0.3s ease;
            
            &.rotated {
              transform: rotate(180deg);
            }
          }
        }
        
        .faq-answer {
          padding: 0 2rem 1.5rem 2rem;
          background: white;
          
          p {
            font-size: 1.3rem;
            color: $landing-text-medium;
            line-height: 1.8;
            font-weight: 400;
            margin: 0;
            padding-left: 45px;
          }
        }
      }
    }
  }
  
  @media (max-width: 768px) {
    .hero-section {
      padding: 100px 24px;
      min-height: 420px;
      background-size: cover;
      background-position: center;

      .hero-title {
        font-size: 2.5rem;
      }
      
      .hero-subtitle {
        font-size: 1.6rem;
      }
      
      .hero-description {
        font-size: 1.3rem;
      }
    }
    
    .approval-process-section,
    .ready-section,
    .benefits-section,
    .resources-section,
    .faq-section {
      padding: 60px 0;
      
      .section-title {
        font-size: 2.5rem;
      }
      
      .section-subtitle {
        font-size: 1.2rem;
      }
    }
    
    .process-step {
      margin-bottom: 2rem;
    }
    
    .faq-accordion .faq-item {
      .faq-question {
        padding: 1.25rem 1.5rem;
        
        .faq-question-content .faq-question-text {
          font-size: 1.05rem;
        }
      }
      
      .faq-answer p {
        font-size: 1rem;
        padding-left: 35px;
      }
    }
  }
}
</style>

